import { useNavigate, useParams } from "@tanstack/react-router";

import { useGrow, useUpdateGrow } from "@/lib/api/grows";
import { ErrorState } from "@/components/error-state";
import { OptimisticLockBanner } from "@/components/optimistic-lock-banner";

import { GrowForm, parseOptNum, type GrowFormValues } from "./form";

export function GrowEditPage() {
  const { growId } = useParams({ from: "/grows/$growId/edit" });
  const navigate = useNavigate();
  const q = useGrow(growId);
  const update = useUpdateGrow(growId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;

  function submit(values: GrowFormValues) {
    if (!q.data) return;
    update.mutate(
      {
        expected_updated_at: q.data.updated_at,
        species: values.species,
        count: Number(values.count),
        system_type: values.system_type,
        reservoir_id: emptyToUndef(values.reservoir_id),
        // Send null (not undefined) when cleared so the PATCH explicitly
        // UNSETS the grow's default water source rather than leaving it as-is.
        water_source_id: emptyToUndef(values.water_source_id) ?? null,
        feeder_class: emptyToUndef(values.feeder_class),
        ph_min: parseOptNum(values.ph_min),
        ph_ideal: parseOptNum(values.ph_ideal),
        ph_max: parseOptNum(values.ph_max),
        ec_min: parseOptNum(values.ec_min),
        ec_ideal: parseOptNum(values.ec_ideal),
        ec_max: parseOptNum(values.ec_max),
        temp_min_c: parseOptNum(values.temp_min_c),
        temp_ideal_c: parseOptNum(values.temp_ideal_c),
        temp_max_c: parseOptNum(values.temp_max_c),
        current_state: emptyToUndef(values.current_state),
        notes: values.notes ?? "",
        plant_profile_id: emptyToUndef(values.plant_profile_id),
        reason: emptyToUndef(values.reason),
      },
      {
        onSuccess: () =>
          navigate({ to: "/grows/$growId", params: { growId } }),
      },
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit grow</h1>
      <GrowForm
        initial={q.data}
        submitting={update.isPending}
        showReason
        submitLabel="Save changes"
        onSubmit={submit}
        banner={
          update.error ? (
            <OptimisticLockBanner
              error={update.error}
              onRefresh={() => {
                update.reset();
                void q.refetch();
              }}
            />
          ) : undefined
        }
      />
      {update.error && !isStale(update.error) && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
    </div>
  );
}

function emptyToUndef(s: string | undefined): string | undefined {
  return s && s.length > 0 ? s : undefined;
}

import { ApiError } from "@/lib/api/client";
function isStale(err: unknown): boolean {
  return err instanceof ApiError && err.isStale;
}
